import { useEffect, useState } from "react";
import { Menu, X, MessageCircle } from "lucide-react";
import { COMPANY } from "../lib/constants";

const navItems = [
  { id: "inicio", label: "Inicio" },
  { id: "servicios", label: "Servicios" },
  { id: "como-funciona", label: "Cómo Funciona" },
  { id: "sobre-nosotros", label: "Sobre Nosotros" },
  { id: "contacto", label: "Contacto" },
];

export default function Header({ onQuoteClick }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState("inicio");

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 12);
      // Update active section
      const sections = navItems.map((n) => document.getElementById(n.id)).filter(Boolean);
      const y = window.scrollY + 140;
      for (let i = sections.length - 1; i >= 0; i--) {
        if (sections[i].offsetTop <= y) {
          setActive(sections[i].id);
          break;
        }
      }
    };
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 w-full bg-white transition-all duration-300 ${
        scrolled ? "shadow-md border-b border-gray-200" : "border-b border-transparent"
      }`}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-28 lg:h-32">
          {/* Logo */}
          <a
            href="#inicio"
            data-testid="header-logo"
            onClick={(e) => { e.preventDefault(); scrollTo("inicio"); }}
            className="flex items-center gap-3"
          >
            <img
              src={COMPANY.logo}
              alt="San José Shipping LLC"
              className="h-24 w-24 sm:h-32 sm:w-32 lg:h-36 lg:w-36 object-contain"
            />
            <div className="hidden sm:flex flex-col leading-tight">
              <span className="font-display font-black text-[#0B1F4D] text-lg sm:text-xl tracking-tight uppercase">
                San José
              </span>
              <span className="font-display font-black text-[#0B1F4D] text-lg sm:text-xl tracking-tight uppercase -mt-1">
                Shipping LLC
              </span>
              <span className="text-[10px] text-gray-500 mt-1 italic">
                Tu compañía de confianza
              </span>
            </div>
          </a>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-8">
            {navItems.map((n) => (
              <button
                key={n.id}
                data-testid={`nav-link-${n.id}`}
                onClick={() => scrollTo(n.id)}
                className={`relative text-sm uppercase tracking-wider font-bold transition-colors ${
                  active === n.id ? "text-[#D61F26]" : "text-[#0B1F4D] hover:text-[#D61F26]"
                }`}
              >
                {n.label}
                {active === n.id && (
                  <span className="absolute -bottom-2 left-0 right-0 h-0.5 bg-[#D61F26]" />
                )}
              </button>
            ))}
          </nav>

          {/* CTA */}
          <div className="flex items-center gap-3">
            <button
              data-testid="header-cotiza-btn"
              onClick={onQuoteClick}
              className="hidden sm:inline-flex items-center gap-2 bg-[#D61F26] text-white px-5 py-3 uppercase font-bold tracking-wider text-xs hover:bg-[#A3161C] transition-all rounded-full shadow-md"
            >
              <MessageCircle className="w-4 h-4" strokeWidth={2.5} />
              Cotiza Tu Envío
            </button>
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen(!open)}
              className="lg:hidden p-2 text-[#0B1F4D]"
              aria-label="Abrir menú"
            >
              {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {open && (
          <div data-testid="mobile-menu" className="lg:hidden border-t border-gray-200 py-4">
            <div className="flex flex-col gap-1">
              {navItems.map((n) => (
                <button
                  key={n.id}
                  data-testid={`mobile-nav-link-${n.id}`}
                  onClick={() => scrollTo(n.id)}
                  className="text-left px-2 py-3 text-sm uppercase tracking-wider font-bold text-[#0B1F4D] hover:bg-gray-50"
                >
                  {n.label}
                </button>
              ))}
              <button
                data-testid="mobile-cotiza-btn"
                onClick={() => { setOpen(false); onQuoteClick && onQuoteClick(); }}
                className="mt-2 inline-flex items-center justify-center gap-2 bg-[#D61F26] text-white px-5 py-3 uppercase font-bold tracking-wider text-xs rounded-full"
              >
                <MessageCircle className="w-4 h-4" /> Cotiza Tu Envío
              </button>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
